import {
  AgentType,
  CheckWebDashboardEditPermission200Response,
  CreateInternalWebDashboard200Response,
  CreateWebDashboardSubscription200Response,
  CreateWebDashboardSubscriptionRequest,
  DefaultApi,
  DeleteWebDashboardSubscription200Response,
  GetAgentThreadRun200Response,
  GetAgentThreads200Response,
  GetSQLiteDownloadURL200Response,
  GetWebDashboard200Response,
  GetWebDashboardForScreenshot200Response,
  GetWebDashboardRun200Response,
  InviteItem,
  ListWebDashboardSubscriptionRuns200Response,
  ListWebDashboardSubscriptions200Response,
  ListWebDashboardShares200Response,
  PatchWebDashboardRequest,
  PatchWebDashboardSubscriptionRequest,
  PresignWebDashboardBlockScreenshot200Response,
  PresignWebDashboardBlockScreenshotRequest,
  PostAgentThread200Response,
  PostAgentThreadRequest,
  RefreshWebDashboard200Response,
  UploadWebDashboardBlockScreenshot200Response,
  UploadWebDashboardBlockScreenshotRequest,
  UpdateWebDashboardRequest,
  UpdateWebDashboardSubscription200Response,
  UpdateWebDashboardSubscriptionRequest,
  UpdateWebDashboardTabsConfig200Response,
  UserExtraType,
  WebDashboardShareInfo,
  WebDashboardSharingLevel,
  WebDashboardTabsConfig,
} from "@coeff/api";
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { useEffect } from "react";

import {
  bumpDashboardWriteEpoch,
  enqueueDashboardWrite,
  getDashboardWriteEpoch,
} from "../../api/dashboardWriteChain";
import { QueryCacheKey } from "../../api/queryClient";
import { isAxiosError, useApiContext } from "../../utils";

export const CREATE_MODE_DASHBOARD_POLL_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

// Minimal shape needed to reason about AI image staleness without depending on
// generated types including the new timestamp fields.
export type DashboardWithAiImageMeta = {
  content_config?: GetWebDashboard200Response["content_config"];
  data_last_changed_dt?: string;
  ai_images_last_generated_dt?: string;
};

export const areAiImagesStaleForDashboard = (
  dashboard: DashboardWithAiImageMeta | undefined
): boolean => {
  if (!dashboard) {
    return false;
  }

  const blocks: any[] = (dashboard.content_config?.content_spec?.blocks as any[]) ?? [];
  const hasAiImages = blocks.some(b => b && b.type === "ai_image");

  const dataLastChanged = dashboard.data_last_changed_dt;
  const aiImagesLastGenerated = dashboard.ai_images_last_generated_dt;

  return (
    hasAiImages &&
    !!dataLastChanged &&
    (!aiImagesLastGenerated || new Date(aiImagesLastGenerated) < new Date(dataLastChanged))
  );
};

const fetchDashboardPermissions = async (
  apiClient: DefaultApi,
  dashboardId: string,
  requestSuperAdminView?: boolean
): Promise<CheckWebDashboardEditPermission200Response> => {
  const response = await apiClient.checkWebDashboardEditPermission(
    { web_dashboard_id: dashboardId },
    requestSuperAdminView ? { params: { view_as_super_admin: 1 } } : undefined
  );
  return response.data;
};

// Fetch permissions for public dashboards (no auth required)
// Returns 403 if dashboard is not public
const fetchPublicDashboardPermissions = async (
  apiClient: DefaultApi,
  dashboardId: string
): Promise<CheckWebDashboardEditPermission200Response> => {
  const response = await apiClient.checkWebDashboardPermissionPublic({
    web_dashboard_id: dashboardId,
  });
  return response.data;
};

const fetchDashboard = async (
  apiClient: DefaultApi,
  dashboardId: string,
  requestSuperAdminView?: boolean
): Promise<GetWebDashboard200Response> => {
  const response = await apiClient.getWebDashboard(
    { web_dashboard_id: dashboardId },
    requestSuperAdminView ? { params: { view_as_super_admin: 1 } } : undefined
  );
  return response.data;
};

// Fetch public dashboard data (no auth required)
// Returns 403 if dashboard is not public
const fetchPublicDashboard = async (
  apiClient: DefaultApi,
  dashboardId: string
): Promise<GetWebDashboard200Response> => {
  const response = await apiClient.getWebDashboardPublic({ web_dashboard_id: dashboardId });
  return response.data;
};

const fetchDashboardForScreenshot = async (
  apiClient: DefaultApi,
  dashboardId: string,
  screenshotToken: string
): Promise<GetWebDashboard200Response> => {
  const response = await apiClient.getWebDashboardForScreenshot({
    web_dashboard_id: dashboardId,
    screenshot_token: screenshotToken,
  });
  return response.data;
};

/**
 * Identity a screenshot capture must render as, or null when there is none.
 *
 * `useDashboard` types its data as the shared dashboard shape, so the screenshot endpoint's
 * extra field needs this narrow accessor. Only the screenshot routes ever populate it.
 */
export const getViewAsEmail = (data?: GetWebDashboard200Response | null): string | null =>
  (data as GetWebDashboardForScreenshot200Response | undefined)?.view_as_email ?? null;

export const getScreenshotSQLiteDownloadUrl = (
  data?: GetWebDashboard200Response | null
): string | null =>
  (data as GetWebDashboardForScreenshot200Response | undefined)?.sqlite_download_url ?? null;

export const fetchWebDashboardSubscriptions = async (
  apiClient: DefaultApi,
  dashboardId: string
): Promise<ListWebDashboardSubscriptions200Response> => {
  const response = await apiClient.listWebDashboardSubscriptions({
    web_dashboard_id: dashboardId,
  });
  return response.data;
};

export const createWebDashboardSubscription = async (
  apiClient: DefaultApi,
  dashboardId: string,
  request: CreateWebDashboardSubscriptionRequest
): Promise<CreateWebDashboardSubscription200Response> => {
  const response = await apiClient.createWebDashboardSubscription({
    web_dashboard_id: dashboardId,
    create_web_dashboard_subscription_request: request,
  });
  return response.data;
};

export const updateWebDashboardSubscription = async (
  apiClient: DefaultApi,
  dashboardId: string,
  subscriptionId: string,
  request: UpdateWebDashboardSubscriptionRequest
): Promise<UpdateWebDashboardSubscription200Response> => {
  const response = await apiClient.updateWebDashboardSubscription({
    web_dashboard_id: dashboardId,
    web_dashboard_subscription_id: subscriptionId,
    update_web_dashboard_subscription_request: request,
  });
  return response.data;
};

export const patchWebDashboardSubscription = async (
  apiClient: DefaultApi,
  dashboardId: string,
  subscriptionId: string,
  request: PatchWebDashboardSubscriptionRequest
): Promise<UpdateWebDashboardSubscription200Response> => {
  const response = await apiClient.patchWebDashboardSubscription({
    web_dashboard_id: dashboardId,
    web_dashboard_subscription_id: subscriptionId,
    patch_web_dashboard_subscription_request: request,
  });
  return response.data;
};

export const deleteWebDashboardSubscription = async (
  apiClient: DefaultApi,
  dashboardId: string,
  subscriptionId: string
): Promise<DeleteWebDashboardSubscription200Response> => {
  const response = await apiClient.deleteWebDashboardSubscription({
    web_dashboard_id: dashboardId,
    web_dashboard_subscription_id: subscriptionId,
  });
  return response.data;
};

export const fetchWebDashboardSubscriptionRuns = async (
  apiClient: DefaultApi,
  dashboardId: string,
  subscriptionId: string
): Promise<ListWebDashboardSubscriptionRuns200Response> => {
  const response = await apiClient.listWebDashboardSubscriptionRuns({
    web_dashboard_id: dashboardId,
    web_dashboard_subscription_id: subscriptionId,
  });
  return response.data;
};

// Only auth/permission failures (401/403) should trigger the public-endpoint
// fallback. Anything else (e.g. a 500) must propagate as-is — falling back would
// surface the public endpoint's 403 and mask the server error as "access denied"
// (ENG-8230).
const isAuthOrPermissionError = (error: unknown): boolean =>
  isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403);

// Helper to try authenticated endpoint first, then public
// Logic:
// 1. Try authenticated endpoint first (for full permissions if logged in)
// 2. If authenticated fails with 401/403 (user not logged in / no access): try public endpoint
// 3. If public also fails: throw the original auth error
export const fetchDashboardPermissionsWithFallback = async (
  apiClient: DefaultApi,
  dashboardId: string,
  requestSuperAdminView?: boolean
): Promise<CheckWebDashboardEditPermission200Response> => {
  try {
    // Try authenticated endpoint first for full permissions
    return await fetchDashboardPermissions(apiClient, dashboardId, requestSuperAdminView);
  } catch (authError: any) {
    if (!isAuthOrPermissionError(authError)) {
      throw authError;
    }
    // Authenticated failed - try public endpoint for public dashboards
    try {
      return await fetchPublicDashboardPermissions(apiClient, dashboardId);
    } catch (publicError: any) {
      // Public also failed - throw the original auth error
      throw authError;
    }
  }
};

// Helper to try authenticated endpoint first, then public
// Logic:
// 1. Try authenticated endpoint first (for full data if logged in)
// 2. If authenticated fails with 401/403 (user not logged in / no access): try public endpoint
// 3. If public also fails: throw the original auth error
export const fetchDashboardWithFallback = async (
  apiClient: DefaultApi,
  dashboardId: string,
  requestSuperAdminView?: boolean
): Promise<GetWebDashboard200Response> => {
  try {
    // Try authenticated endpoint first for full data
    return await fetchDashboard(apiClient, dashboardId, requestSuperAdminView);
  } catch (authError: any) {
    if (!isAuthOrPermissionError(authError)) {
      throw authError;
    }
    // Authenticated failed - try public endpoint for public dashboards
    // If public also fails, prefer the public error (often includes helpful metadata).
    return await fetchPublicDashboard(apiClient, dashboardId);
  }
};

// SQLite fetch helpers

export const fetchSQLiteDownloadUrl = async (
  apiClient: DefaultApi,
  dashboardId: string,
  requestSuperAdminView?: boolean
): Promise<GetSQLiteDownloadURL200Response> => {
  const response = await apiClient.getSQLiteDownloadURL(
    {
      dashboard_id: dashboardId,
    },
    requestSuperAdminView ? { params: { view_as_super_admin: 1 } } : undefined
  );
  return response.data;
};

export const fetchPublicSQLiteDownloadUrl = async (
  apiClient: DefaultApi,
  dashboardId: string
): Promise<GetSQLiteDownloadURL200Response> => {
  const response = await apiClient.getSQLiteDownloadURLPublic({
    dashboard_id: dashboardId,
  });
  return response.data;
};

export const downloadSQLiteDatabaseV2 = async (
  spreadsheetId: string,
  spreadsheetVersionId: number
): Promise<ArrayBuffer> => {
  const url = `/api/web_dashboard/download_sqlite_db?spreadsheet_id=${spreadsheetId}&spreadsheet_version_id=${spreadsheetVersionId}&all_tables=1`;
  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) {
    throw new Error(
      `Failed to load SQLite database (HTTP ${response.status}): ${response.statusText}`
    );
  }
  return response.arrayBuffer();
};

export class SQLiteS3FetchError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "SQLiteS3FetchError";
    this.status = status;
  }
}

export const fetchSQLiteDatabaseFromS3 = async (signedUrl: string): Promise<ArrayBuffer> => {
  const response = await fetch(signedUrl);
  if (!response.ok) {
    throw new SQLiteS3FetchError(
      `Failed to load SQLite database from S3 (HTTP ${response.status}): ${response.statusText}`,
      response.status
    );
  }
  return response.arrayBuffer();
};

export const presignBlockScreenshotUpload = async (
  apiClient: DefaultApi,
  requestId: string,
  request: PresignWebDashboardBlockScreenshotRequest
): Promise<PresignWebDashboardBlockScreenshot200Response> => {
  const response = await apiClient.presignWebDashboardBlockScreenshot({
    request_id: requestId,
    presign_web_dashboard_block_screenshot_request: request,
  });
  return response.data;
};

export const registerBlockScreenshotUpload = async (
  apiClient: DefaultApi,
  requestId: string,
  request: UploadWebDashboardBlockScreenshotRequest
): Promise<UploadWebDashboardBlockScreenshot200Response> => {
  const response = await apiClient.uploadWebDashboardBlockScreenshot({
    request_id: requestId,
    upload_web_dashboard_block_screenshot_request: request,
  });
  return response.data;
};

export const uploadToPresignedS3Post = async (
  uploadUrl: string,
  uploadFields: Record<string, string>,
  file: Blob,
  fileName: string
): Promise<void> => {
  const form = new FormData();
  for (const [k, v] of Object.entries(uploadFields)) {
    form.append(k, v);
  }
  form.append("file", file, fileName);

  const response = await fetch(uploadUrl, { method: "POST", body: form });
  if (response.ok) {
    return;
  }

  const text = await response.text();
  throw new Error(`S3 upload failed (status=${response.status}): ${text || "<empty response>"}`);
};

export const uploadToPresignedS3Multipart = async (
  parts: Array<{ part_number: number; signed_url: string }>,
  partSizeBytes: number,
  file: Blob
): Promise<Array<{ part_number: number; etag: string }>> => {
  const uploadedParts: Array<{ part_number: number; etag: string }> = [];

  for (const part of parts) {
    const start = (part.part_number - 1) * partSizeBytes;
    const end = Math.min(start + partSizeBytes, file.size);
    const chunk = file.slice(start, end);

    const response = await fetch(part.signed_url, { method: "PUT", body: chunk });
    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(
        `S3 multipart upload failed (part=${part.part_number}, status=${response.status}): ${
          text || "<empty response>"
        }`
      );
    }

    const etag = response.headers.get("etag");
    if (!etag) {
      throw new Error(
        `S3 multipart upload missing ETag header (part=${part.part_number}). ` +
          `Bucket CORS must expose the ETag header.`
      );
    }

    uploadedParts.push({ part_number: part.part_number, etag });
  }

  return uploadedParts;
};

export const useDashboardPermissions = (
  dashboardId: string | undefined,
  requestSuperAdminView?: boolean,
  options?: UseQueryOptions<CheckWebDashboardEditPermission200Response>
) => {
  const { apiClient } = useApiContext();

  return useQuery(
    [QueryCacheKey.WEB_DASHBOARD_PERMISSIONS, dashboardId, requestSuperAdminView],
    () => fetchDashboardPermissionsWithFallback(apiClient, dashboardId!, requestSuperAdminView),
    {
      enabled: Boolean(dashboardId),
      ...(options as any),
    }
  );
};

export const useDashboard = (
  dashboardId: string | undefined,
  screenshotToken?: string | null,
  requestSuperAdminView?: boolean,
  options?: UseQueryOptions<GetWebDashboard200Response>
) => {
  const { apiClient } = useApiContext();

  return useQuery(
    screenshotToken
      ? [QueryCacheKey.WEB_DASHBOARD, dashboardId, screenshotToken]
      : [QueryCacheKey.WEB_DASHBOARD, dashboardId],
    () => {
      if (screenshotToken) {
        // Use screenshot access endpoint (validates token, no auth required)
        return fetchDashboardForScreenshot(apiClient, dashboardId!, screenshotToken);
      }
      // Try authenticated endpoint first, fall back to public
      return fetchDashboardWithFallback(apiClient, dashboardId!, requestSuperAdminView);
    },
    {
      enabled: Boolean(dashboardId),
      ...(options as any),
    }
  );
};

// Agent Thread API functions
const postAgentThread = async (
  apiClient: DefaultApi,
  params: PostAgentThreadRequest
): Promise<PostAgentThread200Response> => {
  const response = await apiClient.postAgentThread({
    post_agent_thread_request: params,
  });
  return response.data;
};

const postAgentThreadPause = async (apiClient: DefaultApi, threadId: string): Promise<void> => {
  await apiClient.postAgentThreadPause({ thread_id: threadId });
};

const getAgentThreadRun = async (
  apiClient: DefaultApi,
  threadId: string,
  agentType: AgentType
): Promise<GetAgentThreadRun200Response> => {
  const response = await apiClient.getAgentThreadRun({
    thread_id: threadId,
    agent_type: agentType,
  });
  return response.data;
};

/** Default page size for list queries; included in React Query keys so prefetch vs. consumer page sizes stay distinct. */
const AGENT_THREADS_LIST_DEFAULT_PAGE_SIZE = 2;

const getAgentThreads = async (
  apiClient: DefaultApi,
  agentType: AgentType,
  spreadsheetId?: string,
  webDashboardId?: string,
  pageSize?: number
): Promise<GetAgentThreads200Response> => {
  const response = await apiClient.getAgentThreads({
    updated_dt_offset: undefined,
    sort_order: "desc",
    page_size: pageSize || AGENT_THREADS_LIST_DEFAULT_PAGE_SIZE,
    spreadsheet_id: spreadsheetId,
    web_dashboard_id: webDashboardId,
    agent_type: agentType,
  });
  return response.data;
};

const saveDashboardConfig = async (
  apiClient: DefaultApi,
  webDashboardId: string,
  params: UpdateWebDashboardRequest
): Promise<CreateInternalWebDashboard200Response> => {
  const response = await apiClient.updateWebDashboard({
    web_dashboard_id: webDashboardId,
    update_web_dashboard_request: params,
  });
  return response.data;
};

// ---------------------------------------------------------------------------
// User memory (cross-dashboard AI memory note; UserExtraType.USER_MEMORY)
// ---------------------------------------------------------------------------

const fetchUserMemory = async (apiClient: DefaultApi): Promise<string> => {
  const response = await apiClient.getUserExtra({ user_extra_type: UserExtraType.UserMemory });
  return response.data.user_extra?.user_memory ?? "";
};

const saveUserMemory = async (apiClient: DefaultApi, memory: string): Promise<void> => {
  // Full-blob replace (last-write-wins); an empty string clears the memory.
  await apiClient.setUserExtra({
    set_user_extra_request: {
      user_extra_type: UserExtraType.UserMemory,
      user_extra: { user_memory: memory },
    },
  });
};

export const useUserMemoryQuery = (enabled: boolean) => {
  const { apiClient } = useApiContext();
  return useQuery([QueryCacheKey.USER_MEMORY], () => fetchUserMemory(apiClient), {
    enabled,
  });
};

export const useSaveUserMemoryMutation = () => {
  const { apiClient } = useApiContext();
  const queryClient = useQueryClient();

  return useMutation((memory: string) => saveUserMemory(apiClient, memory), {
    onSuccess: (_data, memory) => {
      // Full-blob write: the new value is known exactly, so set the cache
      // synchronously — callers (e.g. the drawer's clear flow) can rely on
      // savedMemory being fresh the moment mutateAsync resolves, instead of
      // racing the invalidation refetch. Invalidate afterwards for server truth.
      queryClient.setQueryData([QueryCacheKey.USER_MEMORY], memory);
      queryClient.invalidateQueries([QueryCacheKey.USER_MEMORY]);
    },
  });
};

export const usePostAgentThreadMutation = () => {
  const { apiClient } = useApiContext();
  const queryClient = useQueryClient();

  return useMutation((params: PostAgentThreadRequest) => postAgentThread(apiClient, params), {
    onSuccess: (data, variables) => {
      // Invalidate the thread run query so it can be fetched
      queryClient.invalidateQueries([
        QueryCacheKey.AGENT_THREAD_RUN,
        data.thread_id,
        variables.agent_type,
      ]);
      // A confirmed/rejected save_memory decision may have changed the stored
      // memory server-side — refresh the drawer's cached copy so it never shows
      // the pre-confirm note.
      if (variables.save_memory_decision) {
        queryClient.invalidateQueries([QueryCacheKey.USER_MEMORY]);
      }
    },
  });
};

export const usePostAgentThreadPauseMutation = () => {
  const { apiClient } = useApiContext();
  return useMutation((threadId: string) => postAgentThreadPause(apiClient, threadId));
};

export const useGetAgentThreadRun = (
  threadId: string | undefined,
  agentType: AgentType,
  options?: UseQueryOptions<GetAgentThreadRun200Response> & {
    refetchInterval?: number | ((data: GetAgentThreadRun200Response | undefined) => number | false);
  }
) => {
  const { apiClient } = useApiContext();

  return useQuery(
    [QueryCacheKey.AGENT_THREAD_RUN, threadId, agentType],
    () => getAgentThreadRun(apiClient, threadId!, agentType),
    {
      enabled: Boolean(threadId && agentType),
      refetchInterval: options?.refetchInterval,
      ...(options as any),
    }
  );
};

export const usePrefetchAgentThreads = (
  agentType: AgentType | undefined,
  spreadsheetId?: string,
  webDashboardId?: string
) => {
  const { apiClient } = useApiContext();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!agentType || !spreadsheetId || !webDashboardId) {
      return;
    }

    queryClient.prefetchQuery(
      [
        QueryCacheKey.AGENT_THREAD,
        agentType,
        spreadsheetId,
        webDashboardId,
        AGENT_THREADS_LIST_DEFAULT_PAGE_SIZE,
      ],
      () =>
        getAgentThreads(
          apiClient,
          agentType,
          spreadsheetId,
          webDashboardId,
          AGENT_THREADS_LIST_DEFAULT_PAGE_SIZE
        )
    );
  }, [agentType, spreadsheetId, webDashboardId, apiClient, queryClient]);
};

export const useGetAgentThreads = (
  agentType: AgentType,
  spreadsheetId?: string,
  webDashboardId?: string,
  pageSize?: number,
  options?: UseQueryOptions<GetAgentThreads200Response>
) => {
  const { apiClient } = useApiContext();

  const resolvedPageSize = pageSize || AGENT_THREADS_LIST_DEFAULT_PAGE_SIZE;

  return useQuery(
    [QueryCacheKey.AGENT_THREAD, agentType, spreadsheetId, webDashboardId, resolvedPageSize],
    () => getAgentThreads(apiClient, agentType, spreadsheetId, webDashboardId, resolvedPageSize),
    {
      enabled: Boolean(agentType && spreadsheetId && webDashboardId),
      ...(options as any),
    }
  );
};

/**
 * A save failed with 409 web_dashboard_edit_conflict: another writer (often the same
 * user in another tab) changed the dashboard
 * since this client last read it (stale expected_modified_dt). Callers should keep their
 * rollback but show the reload prompt instead of the generic failure toast.
 */
export const isSaveConflictError = (err: unknown): boolean => {
  const response = (err as { response?: { status?: number; data?: { type?: string } } })?.response;

  return response?.status === 409 && response.data?.type === "web_dashboard_edit_conflict";
};

/**
 * A refresh run failed because a concurrent edit aborted it (the edit won, so the
 * refresh is safely retryable). Matches the typed failure_type from newer backends,
 * plus a fallback on the abort reasons embedded in error_message by runs written
 * before failure_type existed. Deliberately does not match "Dashboard was deleted" —
 * that abort can never succeed on retry.
 */
export const isConcurrentEditRefreshAbort = (
  run: Pick<GetWebDashboardRun200Response, "failure_type" | "error_message">
): boolean =>
  run.failure_type === "concurrent_edit" ||
  /Dashboard was modified|New messages in editor thread/.test(run.error_message ?? "");

export const useSaveDashboardConfigMutation = (webDashboardId: string) => {
  const { apiClient } = useApiContext();
  const queryClient = useQueryClient();

  return useMutation(
    (params: UpdateWebDashboardRequest) => {
      // The caller built `params` from the cache just now; snapshot the epoch and token
      // with it. If an out-of-band write (concurrent-change merge, rename) lands before
      // this link executes, the payload predates the row — pairing it with the fresh
      // cache token would token-match and silently full-write over that content, so it
      // must fall back to this enqueue-time token and take the server's rebase/409 path.
      const epochAtEnqueue = getDashboardWriteEpoch(webDashboardId);

      const tokenAtEnqueue = queryClient.getQueryData<GetWebDashboard200Response>([
        QueryCacheKey.WEB_DASHBOARD,
        webDashboardId,
      ])?.modified_dt;

      return enqueueDashboardWrite(webDashboardId, async () => {
        // Attach the concurrency token from the cached dashboard so the server can reject
        // writes built on a stale snapshot (409 web_dashboard_edit_conflict). The token is
        // opaque — echo the exact string, never parse/reformat it.
        const cachedDashboard = queryClient.getQueryData<GetWebDashboard200Response>([
          QueryCacheKey.WEB_DASHBOARD,
          webDashboardId,
        ]);

        const expectedModifiedDt =
          getDashboardWriteEpoch(webDashboardId) === epochAtEnqueue
            ? cachedDashboard?.modified_dt
            : tokenAtEnqueue;

        const data = await saveDashboardConfig(apiClient, webDashboardId, {
          expected_modified_dt: expectedModifiedDt,
          ...params,
        });

        // Patch fresh state into the cache here (not in onSuccess, which runs after
        // this promise resolves) so the next chained save reads the post-save token.
        if (data.merged_concurrent_changes) {
          // The server rebased this edit onto concurrent changes the cache has never
          // seen. Adopt the merged config so payloads built later start from it, and
          // bump the epoch so writes already queued (payloads captured pre-merge) fall
          // back to their enqueue-time tokens and rebase too.
          bumpDashboardWriteEpoch(webDashboardId);
          queryClient.setQueriesData(
            { queryKey: [QueryCacheKey.WEB_DASHBOARD, webDashboardId], exact: false },
            (old: GetWebDashboard200Response | undefined) =>
              old
                ? {
                    ...old,
                    modified_dt: data.modified_dt ?? old.modified_dt,
                    content_config: data.content_config ?? old.content_config,
                    spreadsheet_version_id:
                      data.spreadsheet_version_id ?? old.spreadsheet_version_id,
                  }
                : old
          );
        } else {
          // Adopt the post-save token and version id, and nothing else. The caller already
          // wrote the saved content_config optimistically, and the server returns it
          // unchanged apart from the title/description mirror (which the header editor
          // applies locally too), so there is nothing to pull.
          queryClient.setQueriesData(
            { queryKey: [QueryCacheKey.WEB_DASHBOARD, webDashboardId], exact: false },
            (old: GetWebDashboard200Response | undefined) =>
              old
                ? {
                    ...old,
                    modified_dt: data.modified_dt ?? old.modified_dt,
                    spreadsheet_version_id:
                      data.spreadsheet_version_id ?? old.spreadsheet_version_id,
                  }
                : old
          );
        }

        return data;
      });
    }
    // Deliberately NO onSuccess invalidation. A post-save refetch is a GET that lands some
    // hundreds of ms after the PUT, and it replaces the whole cached dashboard with the
    // server's copy — which predates any optimistic write the user made in between. That is
    // exactly the "toggle flips back" jank from the block edit popover review, and the corner
    // case SourceSheetModal documents for its own toggles. Everything a save changes on the
    // row is patched in above from the response, so the refetch bought nothing.
  );
};

const refreshDashboard = async (
  apiClient: DefaultApi,
  dashboardId: string,
  forceRefresh = false,
  tabsConfig?: WebDashboardTabsConfig,
  excelUploadS3Key?: string,
  spreadsheetName?: string,
  excelUploadClientMetrics?: {
    file_size_bytes: number;
    upload_type: "post" | "multipart";
    upload_duration_ms: number;
    multipart_part_count?: number;
  }
): Promise<RefreshWebDashboard200Response> => {
  const response = await apiClient.refreshWebDashboard({
    web_dashboard_id: dashboardId,
    refresh_web_dashboard_request: {
      force_refresh: forceRefresh,
      tabs_config: tabsConfig,
      excel_upload_s3_key: excelUploadS3Key,
      // Newer servers persist this for local Excel to display the current filename.
      spreadsheet_name: spreadsheetName,
      excel_upload_client_metrics: excelUploadClientMetrics,
    } as any,
  });
  return response.data;
};

const getDashboardRunStatus = async (
  apiClient: DefaultApi,
  dashboardId: string,
  runId: string
): Promise<GetWebDashboardRun200Response> => {
  const response = await apiClient.getWebDashboardRun({
    web_dashboard_id: dashboardId,
    web_dashboard_run_id: runId,
  });
  return response.data;
};

export const useRefreshDashboardMutation = (dashboardId: string | undefined) => {
  const { apiClient } = useApiContext();
  const queryClient = useQueryClient();

  return useMutation(
    ({
      forceRefresh = false,
      tabsConfig,
      excelUploadS3Key,
      spreadsheetName,
      excelUploadClientMetrics,
    }: {
      forceRefresh?: boolean;
      tabsConfig?: WebDashboardTabsConfig;
      excelUploadS3Key?: string;
      spreadsheetName?: string;
      excelUploadClientMetrics?: {
        file_size_bytes: number;
        upload_type: "post" | "multipart";
        upload_duration_ms: number;
        multipart_part_count?: number;
      };
    } = {}) => {
      if (!dashboardId) {
        throw new Error("Dashboard ID is required");
      }
      return refreshDashboard(
        apiClient,
        dashboardId,
        forceRefresh,
        tabsConfig,
        excelUploadS3Key,
        spreadsheetName,
        excelUploadClientMetrics
      );
    },
    {
      onSuccess: data => {
        // Start polling for run status if we have a run_id
        const runId = data.run_id || data.existing_run_id;
        if (runId && dashboardId) {
          queryClient.setQueryData([QueryCacheKey.WEB_DASHBOARD_REFRESH_RUN, dashboardId, runId], {
            status: "queued",
          });
        }
      },
    }
  );
};

/**
 * Saves the tabs config immediately without triggering a refresh (ENG-7204).
 * The saved config applies on the NEXT run (manual or scheduled). The backend
 * returns 409 while a refresh run is in flight and 422 for an all-excluded config.
 */
export const useUpdateWebDashboardTabsConfigMutation = (dashboardId: string | undefined) => {
  const { apiClient } = useApiContext();
  const queryClient = useQueryClient();

  return useMutation(
    async (
      tabsConfig: WebDashboardTabsConfig
    ): Promise<UpdateWebDashboardTabsConfig200Response> => {
      if (!dashboardId) {
        throw new Error("Dashboard ID is required");
      }
      const response = await apiClient.updateWebDashboardTabsConfig({
        web_dashboard_id: dashboardId,
        update_web_dashboard_tabs_config_request: { tabs_config: tabsConfig },
      });
      return response.data;
    },
    {
      onSuccess: data => {
        // Carry the new modified_dt too, so the next content save doesn't take the
        // conflict/rebase path (which clears the undo stack) over a version this save minted.
        //
        // KNOWN, ACCEPTED TRADE-OFF — this breaks the OCC invariant the sibling writers keep,
        // and it does not fully deliver the benefit above:
        //
        //  - Adopting a row-current token without an OCC guard is what
        //    usePatchDashboardSharingLevelMutation deliberately avoids (see its comment). The
        //    endpoint takes no OCC token by design (UpdateWebDashboardTabsConfigResource), so
        //    nothing here proves the cache is row-current. If another editor's content change
        //    landed first, this adopts a fresh token onto a stale content_config, and the next
        //    content save token-matches and silently full-writes over their edit.
        //  - The rebase it aims to avoid can still happen anyway: this PUT is not run through
        //    enqueueDashboardWrite, so it can land between a queued save's token capture and
        //    that save's execution.
        //
        // Fixing it means wrapping the PUT in enqueueDashboardWrite and NOT adopting
        // modified_dt (leaving the stale token so the next save rebases visibly). That trades
        // rare silent loss of a colleague's edit for a more frequent undo-stack clear, so it
        // was left as-is for ENG-7204. Revisit if multi-editor overwrites get reported.
        queryClient.setQueriesData<GetWebDashboard200Response | undefined>(
          { queryKey: [QueryCacheKey.WEB_DASHBOARD, dashboardId], exact: false },
          old =>
            old ? { ...old, tabs_config: data.tabs_config, modified_dt: data.modified_dt } : old
        );
      },
    }
  );
};

export const useDashboardRunStatus = (
  dashboardId: string | undefined,
  runId: string | null,
  options?: UseQueryOptions<GetWebDashboardRun200Response> & {
    refetchInterval?:
      | number
      | ((data: GetWebDashboardRun200Response | undefined) => number | false);
    onCompleted?: () => void;
    onFailed?: (errorMessage?: string) => void;
  }
) => {
  const { apiClient } = useApiContext();

  return useQuery(
    [QueryCacheKey.WEB_DASHBOARD_REFRESH_RUN, dashboardId, runId],
    () => getDashboardRunStatus(apiClient, dashboardId!, runId!),
    {
      enabled: Boolean(dashboardId && runId),
      refetchInterval: options?.refetchInterval,
      // Keep polling while the tab is backgrounded. React Query otherwise pauses the
      // refetch interval on blur, so a refresh that completes while the user is on another
      // tab is never observed and the refresh snackbar stays up until they refocus.
      refetchIntervalInBackground: true,
      ...(options as any),
    }
  );
};

const getLatestDashboardRun = async (
  apiClient: DefaultApi,
  dashboardId: string
): Promise<GetWebDashboardRun200Response> => {
  const response = await apiClient.getLatestWebDashboardRun({
    web_dashboard_id: dashboardId,
  });
  return response.data;
};

export const useLatestDashboardRun = (
  dashboardId: string | undefined,
  options?: UseQueryOptions<GetWebDashboardRun200Response>
) => {
  const { apiClient } = useApiContext();
  return useQuery(
    [QueryCacheKey.WEB_DASHBOARD_LATEST_RUN, dashboardId],
    () => getLatestDashboardRun(apiClient, dashboardId!),
    {
      enabled: Boolean(dashboardId),
      staleTime: 0,
      refetchOnWindowFocus: false,
      ...(options as any),
    }
  );
};

// Super admin API hooks

/**
 * Fetch editor thread messages for a dashboard (super admin only).
 * Uses the retool admin endpoint.
 */
export const useSuperAdminViewEditorThreadMessages = (
  dashboardId: string | undefined,
  enabled = true
) => {
  const { apiClient } = useApiContext();

  return useQuery(
    ["editorThreadMessages", dashboardId],
    async () => {
      if (!dashboardId) {
        return {
          thread_id: null,
          messages: [],
          initial_version_s3_path: undefined,
          dashboard_created_dt: undefined,
        };
      }
      const response = await apiClient.getEditorThreadMessages({
        dashboard_id: dashboardId,
      });
      return response.data;
    },
    {
      enabled: Boolean(dashboardId) && enabled,
      staleTime: 30000, // 30 seconds
    }
  );
};

/**
 * Load a dashboard version from S3 (super admin only).
 * Returns the full web_dashboard_json and metadata.
 */
export const useLoadDashboardVersionMutation = () => {
  const { apiClient } = useApiContext();

  return useMutation(async (params: { s3Url: string; webDashboardId: string }) => {
    try {
      const response = await apiClient.loadDashboardVersion({
        load_dashboard_version_request: {
          s3_url: params.s3Url,
          web_dashboard_id: params.webDashboardId,
        },
      });
      return response.data;
    } catch (error) {
      console.error("[loadDashboardVersion] Error:", error);
      throw error;
    }
  });
};

// --- Dashboard sharing hooks ---

export const useDashboardShares = (
  dashboardId: string | undefined,
  requestSuperAdminView?: boolean,
  options?: UseQueryOptions<ListWebDashboardShares200Response>
) => {
  const { apiClient } = useApiContext();

  return useQuery(
    [QueryCacheKey.WEB_DASHBOARD_SHARES, dashboardId, requestSuperAdminView],
    async (): Promise<ListWebDashboardShares200Response> => {
      const response = await apiClient.listWebDashboardShares(
        {
          web_dashboard_id: dashboardId!,
        },
        requestSuperAdminView ? { params: { view_as_super_admin: 1 } } : undefined
      );
      return response.data;
    },
    {
      enabled: Boolean(dashboardId),
      ...(options as any),
    }
  );
};

export const useInviteDashboardShareMutation = (dashboardId: string | undefined) => {
  const { apiClient } = useApiContext();
  const queryClient = useQueryClient();

  return useMutation(
    async (invites: InviteItem[]) => {
      if (!dashboardId) {
        throw new Error("Dashboard ID is required");
      }
      const response = await apiClient.inviteWebDashboardShare({
        web_dashboard_id: dashboardId,
        invite_web_dashboard_share_request: { invites },
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARD_SHARES, dashboardId]);
      },
    }
  );
};

export const usePatchDashboardSharingLevelMutation = (dashboardId: string | undefined) => {
  const { apiClient } = useApiContext();
  const queryClient = useQueryClient();

  return useMutation(
    async (sharingLevel: WebDashboardSharingLevel) => {
      if (!dashboardId) {
        throw new Error("Dashboard ID is required");
      }
      // Chained with saves: this PATCH bumps modified_dt, so landing mid-flight would 409
      // an in-flight save built on the pre-PATCH token.
      return await enqueueDashboardWrite(dashboardId, async () => {
        // OCC-guard the PATCH with the cached token. Without it a 200 here would adopt a
        // fresh token into a cache whose content_config may predate another writer — the
        // next save would then token-match and silently full-write over that content.
        // A 409 keeps the stale token in place so that save rebases or conflicts visibly.
        const cachedDashboard = queryClient.getQueryData<GetWebDashboard200Response>([
          QueryCacheKey.WEB_DASHBOARD,
          dashboardId,
        ]);

        const response = await apiClient.patchWebDashboard({
          web_dashboard_id: dashboardId,
          patch_web_dashboard_request: {
            sharing_level: sharingLevel,
            expected_modified_dt: cachedDashboard?.modified_dt,
          },
        });

        // Update caches in place so the viewer does not refetch the full dashboard (no
        // refresh flash). Done inside the chain link (not onSuccess) so the next chained
        // save reads the post-PATCH concurrency token — safe to adopt because the OCC
        // guard above proved the cache was row-current. No write-epoch bump:
        // sharing_level is not part of a save's content_config payload, so a queued
        // full-write can't revert it.
        queryClient.setQueriesData<CheckWebDashboardEditPermission200Response>(
          { queryKey: [QueryCacheKey.WEB_DASHBOARD_PERMISSIONS, dashboardId], exact: false },
          old => (old ? { ...old, sharing_level: sharingLevel } : old)
        );
        queryClient.setQueriesData<GetWebDashboard200Response>(
          { queryKey: [QueryCacheKey.WEB_DASHBOARD, dashboardId], exact: false },
          old =>
            old
              ? {
                  ...old,
                  sharing_level: sharingLevel,
                  modified_dt: response.data.modified_dt ?? old.modified_dt,
                }
              : old
        );
        return response.data;
      });
    },
    {
      onError: () => {
        // Likely a 409 from the OCC guard: the dashboard changed since this tab last
        // read it. Refetch so the cached token/config resnap to the row and a retry
        // can succeed.
        queryClient.invalidateQueries({
          queryKey: [QueryCacheKey.WEB_DASHBOARD, dashboardId],
          exact: false,
        });
      },
    }
  );
};
